import api from '../../../config/axios';

/**
 * Livello di accesso API per la dashboard ESXi ("Ambienti di test").
 *
 * Riusa l'istanza Axios condivisa (`config/axios`), che gestisce già baseURL,
 * token Bearer e gestione delle sessioni scadute: qui non si duplica alcuna
 * logica di autenticazione.
 *
 * Espone esclusivamente gli endpoint del contratto documentato
 * (backend/docs/esxi-api.md):
 *   GET  /esxi/summary
 *   GET  /esxi/vms
 *   GET  /esxi/vms/:catalogId
 *   POST /esxi/vms/:catalogId/actions   (solo powerOn | shutdown)
 *   GET  /esxi/operations/:operationId
 *
 * Ogni metodo accetta un `signal` opzionale (AbortController) per annullare le
 * richieste diventate obsolete.
 */

const BASE = '/esxi';
const withRefresh = (refresh) => (refresh ? { refresh: 'true' } : undefined);

/** Riepilogo host: stato, conteggi VM e anomalie di catalogo. */
export const fetchSummary = (refresh = false, signal) =>
  api
    .get(`${BASE}/summary`, { params: withRefresh(refresh), signal })
    .then((r) => r.data);

/**
 * Elenco VM registrate, con filtri server-side opzionali.
 * @param {Object} filters { environment, product, role, powerState, search, refresh }
 */
export const fetchVms = (filters = {}, signal) => {
  const { environment, product, role, powerState, search, refresh } = filters;
  const params = {};
  if (environment) params.environment = environment;
  if (product) params.product = product;
  if (role) params.role = role;
  if (powerState) params.powerState = powerState;
  if (search) params.search = search;
  if (refresh) params.refresh = 'true';
  return api.get(`${BASE}/vms`, { params, signal }).then((r) => r.data);
};

/** Dettaglio runtime + catalogo di una singola VM catalogata. */
export const fetchVmDetail = (catalogId, signal) =>
  api
    .get(`${BASE}/vms/${encodeURIComponent(catalogId)}`, { signal })
    .then((r) => r.data);

/**
 * Crea un'operazione asincrona su una VM. Il contratto accetta solo:
 *   { action: 'powerOn' | 'shutdown', reason?: string }
 * Restituisce l'operazione appena creata (di norma in stato QUEUED).
 */
export const createVmAction = (catalogId, action, reason, signal) => {
  const body = { action };
  if (reason && String(reason).trim()) body.reason = String(reason).trim();
  return api
    .post(`${BASE}/vms/${encodeURIComponent(catalogId)}/actions`, body, {
      signal,
    })
    .then((r) => r.data);
};

/** Stato corrente di un'operazione asincrona. */
export const fetchOperation = (operationId, signal) =>
  api
    .get(`${BASE}/operations/${encodeURIComponent(operationId)}`, { signal })
    .then((r) => r.data);
